import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ALERT_CENTER_NS } from 'src/alerts/AlertCenter.constants';
import type { tMetaPricingVideo } from 'src/alerts/MetaPricingNotice.functions';
import './MetaPricingVideo.styles.scss';

export type tMetaPricingVideoPlayer = 'inline' | 'new_tab';

export type tMetaPricingVideoProps = {
  video: tMetaPricingVideo;
  /** Fired on the press: `inline` when the player mounts, `new_tab` for a link host. */
  onPlay: (player: tMetaPricingVideoPlayer) => void;
  /**
   * The i18n prefix of the caption, the play label and the fallback link
   * (`META_PRICING_VIDEO_COPY_PREFIXES`): each message names its own video.
   */
  copyPrefix: string;
};

const IFRAME_ALLOW =
  'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';

function PlayIcon() {
  return (
    <span className="MetaPricingVideo__PlayIcon" aria-hidden="true">
      <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
        <path d="M8 5v14l11-7z" />
      </svg>
    </span>
  );
}

/**
 * AGI-428, messages 2 (25-sep) and 3 (6-oct): the notice's video, played
 * INSIDE it ("dale play y se reproduce ahi mismo").
 *
 * A facade first (a 16:9 box with a play button) and the iframe only after
 * the press, with autoplay: the pattern `AccountHealthDetail` already runs in
 * production for the spam explainer. Mounting a YouTube or Drive iframe on
 * every automatic open would cost every owner a third-party page load for a
 * video most will not press, and would start the player's cookies before
 * anyone asked for it.
 *
 * A host that cannot be embedded keeps the same box as a link to a new tab,
 * so the message still reads the same; the event says which one played.
 */
export default function MetaPricingVideo(props: tMetaPricingVideoProps) {
  const { video, onPlay, copyPrefix } = props;
  const { t } = useTranslation(ALERT_CENTER_NS);
  const [isPlaying, setIsPlaying] = useState(false);
  const caption = t(`${copyPrefix}.caption`);
  const playLabel = t(`${copyPrefix}.play`);

  const renderStage = () => {
    if (video.kind === 'link') {
      return (
        <a
          className="MetaPricingVideo__Facade"
          href={video.watchUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={playLabel}
          data-testid="meta-pricing-video-play"
          onClick={() => onPlay('new_tab')}
        >
          <PlayIcon />
        </a>
      );
    }
    if (isPlaying) {
      return (
        <iframe
          className="MetaPricingVideo__Frame"
          src={video.embedUrl}
          title={caption}
          allow={IFRAME_ALLOW}
          allowFullScreen
          data-testid="meta-pricing-video-frame"
        />
      );
    }
    return (
      <button
        type="button"
        className="MetaPricingVideo__Facade"
        aria-label={playLabel}
        data-testid="meta-pricing-video-play"
        onClick={() => {
          setIsPlaying(true);
          onPlay('inline');
        }}
      >
        <PlayIcon />
      </button>
    );
  };

  return (
    <figure
      className="MetaPricingVideo"
      data-testid="meta-pricing-video-player"
    >
      {renderStage()}
      <figcaption className="MetaPricingVideo__Caption">
        <span>{caption}</span>
        {/* Some browsers and extensions block embedded players; the same
            video is always one click away. */}
        {video.kind === 'embed' ? (
          <span className="MetaPricingVideo__Fallback">
            <Trans
              t={t}
              i18nKey={`${copyPrefix}.fallback`}
              components={[
                <a
                  key="0"
                  href={video.watchUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                />,
              ]}
            />
          </span>
        ) : null}
      </figcaption>
    </figure>
  );
}
